import { Artist, POI, Show, Map } from '../model/FestivalData';
import { generateCanonicalName, generateIdentifier } from '../utils/identifiers';
import { currentMessages } from '../i18n/I18n';

interface ValidationResult<T> {
  isValid: boolean;
  data: T[];
  errors: string[];
}

const NO_MUSIC_LINKS: Artist['music_preview'] = {
  spotify_url: null,
  apple_music_url: null,
  youtube_url: null,
  soundcloud_url: null,
  other_url: null,
};

export class DataImportService {
  static validateArtist(artist: any, artists: Artist[]): ValidationResult<Artist> {
    const t = currentMessages('dataImport');
    const errors: string[] = [];
    const requiredFields = ['name', 'description', 'style'];
    const missingFields = requiredFields.filter(field => !(field in artist));
    
    if (missingFields.length > 0) {
      errors.push(t.artistMissingFields(artist.name || t.unknown, missingFields.join(', ')));
    }

    // Validate music preview URLs if they exist
    if (artist.music_preview) {
      const { spotify_url, apple_music_url, youtube_url, soundcloud_url, other_url } = artist.music_preview;
      
      if (spotify_url && !this.isValidSpotifyUrl(spotify_url)) {
        errors.push(t.invalidLink('Spotify', artist.name));
      }
      
      if (apple_music_url && !this.isValidAppleMusicUrl(apple_music_url)) {
        errors.push(t.invalidLink('Apple Music', artist.name));
      }
      
      if (youtube_url && !this.isValidYoutubeUrl(youtube_url)) {
        errors.push(t.invalidLink('YouTube', artist.name));
      }

      if (soundcloud_url && !this.isValidSoundcloudUrl(soundcloud_url)) {
        errors.push(t.invalidLink('SoundCloud', artist.name));
      }

      if (other_url && !this.isValidUrl(other_url)) {
        errors.push(t.invalidOtherLink(artist.name));
      }
    }

    const isValid = errors.length === 0;
    const data = isValid ? [{
      ...artist,
      identifier: artist.identifier || generateIdentifier(generateCanonicalName(artist.name)),
      // The API needs every link key, even empty ones
      music_preview: { ...NO_MUSIC_LINKS, ...artist.music_preview },
    }] : [];

    return { isValid, data, errors };
  }

  static validatePOI(poi: any, maps: Map[]): ValidationResult<POI> {
    const t = currentMessages('dataImport');
    const errors: string[] = [];
    const requiredFields = ['name', 'category', 'map_identifier', 'relative_coordinates', 'description'];
    const missingFields = requiredFields.filter(field => !(field in poi));
    
    if (missingFields.length > 0) {
      errors.push(t.poiMissingFields(poi.name || t.unknown, missingFields.join(', ')));
    }

    // Validate map_identifier exists
    if (poi.map_identifier && !maps.some(map => map.identifier === poi.map_identifier)) {
      errors.push(t.poiUnknownMap(poi.name, poi.map_identifier));
    }

    // Validate coordinates
    if (poi.relative_coordinates) {
      const { x, y } = poi.relative_coordinates;
      if (typeof x !== 'number' || typeof y !== 'number' || x < 0 || x > 1 || y < 0 || y > 1) {
        errors.push(t.poiInvalidCoordinates(poi.name));
      }
    }

    const isValid = errors.length === 0;
    const data = isValid ? [{
      ...poi,
      identifier: poi.identifier || generateIdentifier(generateCanonicalName(poi.name))
    }] : [];

    return { isValid, data, errors };
  }

  static validateShow(show: any, artists: Artist[], pois: POI[]): ValidationResult<Show> {
    const t = currentMessages('dataImport');
    const errors: string[] = [];
    const requiredFields = ['scene_id', 'artist_id', 'start_date', 'end_date'];
    const missingFields = requiredFields.filter(field => !(field in show));
    
    if (missingFields.length > 0) {
      errors.push(t.showMissingFields(missingFields.join(', ')));
    }

    // Validate artist_id exists
    if (show.artist_id && !artists.some(artist => artist.identifier === show.artist_id)) {
      errors.push(t.showUnknownArtist(show.artist_id));
    }

    // Validate scene_id exists
    if (show.scene_id && !pois.some(poi => poi.identifier === show.scene_id)) {
      errors.push(t.showUnknownScene(show.scene_id));
    }

    // Validate dates
    try {
      const startDate = new Date(show.start_date);
      const endDate = new Date(show.end_date);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        errors.push(t.showInvalidDates);
      } else if (endDate <= startDate) {
        errors.push(t.showEndsBeforeStart);
      }
    } catch {
      errors.push(t.showInvalidDateFormat);
    }

    const isValid = errors.length === 0;
    const data = isValid ? [{
      ...show,
      identifier: show.identifier || generateIdentifier(`${show.artist_id}-${show.start_date}`)
    }] : [];

    return { isValid, data, errors };
  }

  static parseJsonFile(content: string): any {
    try {
      return JSON.parse(content);
    } catch (error) {
      throw new Error(currentMessages('dataImport').parseError);
    }
  }

  private static isValidSpotifyUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.hostname.includes('spotify.com') && 
             ['/track/', '/album/', '/artist/'].some(path => parsedUrl.pathname.includes(path));
    } catch {
      return false;
    }
  }

  private static isValidAppleMusicUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.hostname === 'music.apple.com' && 
             ['/album/', '/artist/'].some(path => parsedUrl.pathname.includes(path));
    } catch {
      return false;
    }
  }

  private static isValidYoutubeUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      // Handle www.youtube.com URLs
      if (parsedUrl.hostname === 'www.youtube.com' || parsedUrl.hostname === 'youtube.com') {
        return parsedUrl.searchParams.has('v');
      }
      // Handle youtu.be URLs
      if (parsedUrl.hostname === 'youtu.be') {
        return parsedUrl.pathname.length > 1;
      }
      return false;
    } catch {
      return false;
    }
  }

  private static isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  private static isValidSoundcloudUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.hostname === 'soundcloud.com';
    } catch {
      return false;
    }
  }
} 